import React, { ReactNode, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import useBundleTranslation from 'i18n';
import Moment from 'moment';
import { DatasetInstance } from 'components/dataset-viewer';
import { Popup } from 'components/common/popup/Popup';
import { BodyLoadingPlaceholder } from 'components/common/loading-placeholder/LoadingPlaceholder';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import GenerateAlertEventsForm from '../report-alert-rule-wizard/GenerateAlertEventsForm';
import { reportAlertRuleAPI } from 'api/viewer/report-alert-rule';
import toast from 'app/toast';
import { useQuery } from '@tanstack/react-query';

export default function GenerateAlertEventsButton({
    elementId,
    alertRuleId,
    children,
    disabled = false,
    beforeGenerate,
}: {
    elementId: number;
    alertRuleId?: number;
    children: ReactNode;
    disabled?: boolean;
    beforeGenerate?: () => Promise<boolean>;
}) {
    const { t } = useBundleTranslation(['app/report/report-alert-rule']);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const {
        data: instancesData,
        isFetching,
        refetch: fetchInstances,
    } = useQuery({
        queryKey: ['alertEventsInstances', elementId],
        queryFn: async () => {
            const response = await reportAlertRuleAPI.getInstancesData(elementId);
            if (response.status !== 'OK') {
                throw new Error(response.message ?? t('generate_alert_events_error'));
            }
            return response.data;
        },
        enabled: false,
        staleTime: Infinity,
    });

    const [maxDateStr, setMaxDateStr] = useState<string | null>(null);
    useEffect(() => {
        if (!instancesData || instancesData.datasetInstances.length <= 1) {
            return;
        }
        const maxUnix = Math.max(...instancesData.datasetInstances.map((i) => Moment(i.measurement_time_raw).unix()));
        setMaxDateStr(Moment.unix(maxUnix).format('YYYY-MM-DD'));
    }, [instancesData?.datasetInstances]);

    useEffect(() => {
        formData.form.hookFormSetValue('date_from', maxDateStr);
        formData.form.hookFormSetValue('date_to', maxDateStr);
    }, [maxDateStr]);

    const isLoading = isFetching || isGenerating;
    const datasetInstances: DatasetInstance[] = instancesData?.datasetInstances ?? [];
    const withHistory: boolean = instancesData?.withHistory ?? false;

    const formData = useCustomSimplifiedForm({
        generate_alert_events: 'Y',
        date_from: null,
        date_to: null,
    });

    const handleClick = async () => {
        if (isLoading || disabled) {
            return;
        }

        if (beforeGenerate) {
            const isSaved = await beforeGenerate();
            if (!isSaved) {
                return;
            }
        }

        let data = instancesData;
        if (!data) {
            const result = await fetchInstances();
            if (result.error || !result.data) {
                toast('error', t('error'), t('generate_alert_events_error'));
                return;
            }
            data = result.data;
        }

        if (!data.withHistory) {
            await runGenerate();
            return;
        }

        setIsPopupOpen(true);
    };

    const runGenerate = async (dateFrom?: string, dateTo?: string) => {
        setIsGenerating(true);
        try {
            const response = await reportAlertRuleAPI.generateAlertEvents(elementId, alertRuleId, dateFrom, dateTo);
            if (response.status == 'OK') {
                toast('success', t('success'), t('generate_alert_events_success'));
            } else {
                toast('error', t('error'), response.message ?? t('generate_alert_events_error'));
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleConfirm = async () => {
        const dateFrom = formData.form.hookFormGetValues('date_from') ?? maxDateStr;
        const dateTo = formData.form.hookFormGetValues('date_to') ?? maxDateStr;
        await runGenerate(dateFrom, dateTo);
        setIsPopupOpen(false);
    };

    return (
        <>
            {isLoading && <BodyLoadingPlaceholder />}
            <Box sx={{ position: 'relative', display: 'inline-flex' }} onClick={handleClick}>
                {children}
            </Box>
            {isPopupOpen && (
                <Popup
                    open={isPopupOpen}
                    onHide={() => setIsPopupOpen(false)}
                    onConfirm={handleConfirm}
                    settings={{ title: t('generate_alert_events'), textOK: t('generate') }}
                >
                    <GenerateAlertEventsForm
                        elementProps={formData}
                        datasetInstances={datasetInstances}
                        withHistory={withHistory}
                        dateOnly={true}
                    />
                </Popup>
            )}
        </>
    );
}
